import { Chip } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import effects from 'app/effects/effects'
import clsx from 'clsx'
import { getLocalizedDate } from 'commons/date'
import { PlanId, getPlanType, isPlanFreeTrialByPlanId } from 'commons/plan'
import * as R from 'ramda'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useTableFetch } from 'commons/reactTableFetch'

import { PlanBillingCard } from './PlanBillingCard'
import { PlanBillingTable } from './PlanBillingTable'
import { usePlanBillingContext } from '../usePlanBillingContext'
import {
  TransactionAction,
  getDescriptionLabel,
} from './PlanBillingUsageHistoryPanel'

// eslint-disable-next-line
const BillingHistoryTransactionAction = {
  TERMINATED: 'TERMINATED',
  UPGRADE: 'UPGRADE',
  DOWNGRADE: 'UPGRADE',
  RENEW: 'RENEW',
}

const useStyles = makeStyles((theme) => ({
  textPositive: {
    color: theme.palette.billing.positive,
  },
  textNegative: {
    color: theme.palette.negative.main,
  },
  success: {
    backgroundColor: '#4fd975',
  },
  failure: {
    backgroundColor: theme.palette.negative.main,
    color: '#FFFFFF',
  },
  default: {
    backgroundColor: '#9C9C9C',
  },
}))

const PaymentType = {
  CREDIT_CARD: 'CREDIT_CARD',
  CASH: 'CASH',
  PAYPAL_ACCOUNT: 'PAYPAL_ACCOUNT',
  NONE: 'NONE',
}

const PaymentStatusLabel = {
  SUCCESS: 'planAndBilling.billingHistory.paymentStatus.success',
  FAIL: 'planAndBilling.billingHistory.paymentStatus.declined',
  REJECT: 'planAndBilling.billingHistory.paymentStatus.reject',
  CANCEL: 'planAndBilling.billingHistory.paymentStatus.cancel',
  NOT_REQUIRED: 'planAndBilling.billingHistory.paymentStatus.notRequired',
}

const getTransactionDate = ({ getTransactionProp, formatMessage }) => {
  return getLocalizedDate(getTransactionProp('createdAt'), 'DATE_TIME')
}

const getTransactionMethod = ({ getTransactionProp, formatMessage }) => {
  switch (getTransactionProp('paymentType')) {
    case PaymentType.CREDIT_CARD: {
      return `${getTransactionProp('cardType')}-${getTransactionProp('last4')}`
    }
    case PaymentType.PAYPAL_ACCOUNT: {
      return formatMessage({ id: 'planAndBilling.billingHistory.paypal' })
    }
    case PaymentType.CASH: {
      return formatMessage({ id: 'planAndBilling.billingHistory.cash' })
    }
    default: {
      return '-'
    }
  }
}

const getTransactionPaymentStatus = ({ getTransactionProp }) => {
  // check if plan is free or free trial
  if (
    R.includes(getTransactionProp('planId'), [
      PlanId.FREE_TRIAL,
      PlanId.FREE_TIER,
    ])
  ) {
    return 'NOT_REQUIRED'
  }

  return getTransactionProp('paymentStatus')
}

const getTransactionDescription = ({ getTransactionProp, formatMessage }) => {
  if (
    isPlanFreeTrialByPlanId(
      getTransactionProp(['actionInfo', 'plan', 'planId']),
    )
  ) {
    return formatMessage({
      id: 'planAndBilling.usageHistory.table.startedFreeTrial.label',
    })
  }

  if (
    R.includes(getTransactionProp('action'), [
      TransactionAction.DOWNGRADE,
      TransactionAction.UPGRADE,
    ])
  ) {
    return formatMessage(
      {
        id: getDescriptionLabel(getTransactionProp('action')),
      },
      {
        name: getTransactionProp('description'),
      },
    )
  }

  return getTransactionProp('description')
}

const getTransactionSubscriptionTerm = ({
  getTransactionProp,
  formatMessage,
}) => {
  if (
    isPlanFreeTrialByPlanId(
      getTransactionProp(['actionInfo', 'plan', 'planId']),
    )
  ) {
    return '-'
  }

  return formatMessage(
    {
      id: 'planAndBilling.usageHistory.table.subscriptionTerm.type',
    },
    {
      type: getPlanType(getTransactionProp('planId')),
    },
  )
}

const formatBillingHistory = ({ pageInfo, formatMessage }) => {
  return R.propOr([], 'data', pageInfo).map((transaction) => {
    const getTransactionProp = R.ifElse(
      R.is(Array),
      R.path(R.__, transaction),
      R.prop(R.__, transaction),
    )

    return {
      date: getTransactionDate({ getTransactionProp, formatMessage }),
      plan: getTransactionDescription({ getTransactionProp, formatMessage }),
      subscriptionTerm: getTransactionSubscriptionTerm({
        getTransactionProp,
        formatMessage,
      }),
      method: getTransactionMethod({ getTransactionProp, formatMessage }),
      paymentStatus:
        PaymentStatusLabel[getTransactionPaymentStatus({ getTransactionProp })],
      amount: getTransactionProp('amount'),
    }
  })
}

const fetchData = (page) => {
  return effects.apis.getBillingHistory({
    page: page.currentPage,
    limit: page.pageSize,
  })
}

export function PlanBillingHistoryPanel() {
  const { formatMessage } = useIntl()

  const { state: planBillingState } = usePlanBillingContext()

  const tableFetch = useTableFetch(fetchData)
  const {
    pageInfo,
    refetchData,
    onFetchData,
    defaultPageSize,
    showPagination,
  } = tableFetch

  React.useEffect(() => {
    if (R.prop('fetchedAt', planBillingState)) {
      refetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [R.prop('fetchedAt', planBillingState)])
  return (
    <PlanBillingCard labelId="planAndBilling.billingHistory.label">
      <PlanBillingTable
        minRows={0}
        className={clsx('-highlight h-full overflow-hidden')}
        FilterComponent={null}
        loading={pageInfo.isLoading}
        data={formatBillingHistory({ pageInfo, formatMessage })}
        total={pageInfo.totalData}
        pages={pageInfo.total}
        manual
        onFetchData={onFetchData}
        defaultPageSize={defaultPageSize}
        showPagination={showPagination}
        noDataLabel={formatMessage({
          id: 'planAndBilling.billingHistory.table.empty.label',
        })}
        columns={[
          {
            Header: formatMessage({
              id: 'planAndBilling.billingHistory.table.date.label',
            }),
            accessor: 'date',
            sortable: false,
          },
          {
            Header: formatMessage({
              id: 'planAndBilling.billingHistory.table.plan.label',
            }),
            accessor: 'plan',
            sortable: false,
            type: 'text',
          },
          // {
          //   Header: formatMessage({
          //     id: 'planAndBilling.billingHistory.table.method.label',
          //   }),
          //   accessor: 'method',
          //   sortable: false,
          //   type: 'text',
          // },
          {
            Header: formatMessage({
              id: 'planAndBilling.billingHistory.table.method.label',
            }),
            accessor: 'method',
            sortable: false,
            type: 'text',
          },
          {
            id: 'roles',
            Header: formatMessage({
              id: 'planAndBilling.billingHistory.table.paymentStatus.label',
            }),
            Cell: (row) => <BillingHistoryPaymentStatus value={row.value} />,
            accessor: 'paymentStatus',
            sortable: false,
            type: 'text',
          },
          {
            Header: formatMessage({
              id: 'planAndBilling.billingHistory.table.amount.label',
            }),
            accessor: 'amount',
            Cell: (row) => (
              <BillingHistoryAmount original={row.original} value={row.value} />
            ),
            className: 'flex justify-end',
            sortable: false,
            type: 'text',
          },
        ]}
      />
    </PlanBillingCard>
  )
}

const BillingHistoryAmount = ({ original, value }) => {
  const isPaymentStatusCancel = R.propOr(
    '',
    'paymentStatus',
    original,
  ).includes('cancel')

  return (
    <span>
      $
      {Number(isPaymentStatusCancel ? 0 : value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
      })}
    </span>
  )
}

const BillingHistoryPaymentStatus = ({ value }) => {
  const classes = useStyles()
  const valueEquals = R.equals(value)
  const isPaymentStatusFailureOrCancel = R.anyPass([
    R.equals(PaymentStatusLabel.FAIL),
    R.equals(PaymentStatusLabel.CANCEL),
  ])

  if (!value) return '-'

  return (
    <Chip
      label={<FormattedMessage id={value} />}
      className={clsx('text-white ', {
        [classes.failure]: isPaymentStatusFailureOrCancel(value),
        [classes.success]: valueEquals(PaymentStatusLabel.SUCCESS),
        [classes.default]: valueEquals(PaymentStatusLabel.NOT_REQUIRED),
      })}
      size="small"
    />
  )
}
